import React, { useState, useEffect } from "react";
import { requestOfflineCache, checkOfflineCache } from "../../registerSW";

export function MissionWelcome({ tour, onStart }) {
  const [showStartingPoint, setShowStartingPoint] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [downloadingOffline, setDownloadingOffline] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [checkingCache, setCheckingCache] = useState(true);

  // Check if tour is already cached on mount
  useEffect(() => {
    const getOfflineAssetsFile = () => {
      if (tour.slug?.includes('mexico-city')) {
        return '/offline-assets-mexico-city.json';
      } else if (tour.slug === 'chicago') {
        return '/offline-assets-chicago.json';
      } else if (tour.slug?.includes('san-juan')) {
        return '/offline-assets-san-juan.json';
      }
      // Default to core only
      return null;
    };

    const checkCache = async () => {
      const tourAssetsFile = getOfflineAssetsFile();
      const assetsToCheck = tourAssetsFile
        ? ['/offline-assets-core.json', tourAssetsFile]
        : ['/offline-assets-core.json'];

      const result = await checkOfflineCache(assetsToCheck);
      if (result.cached) {
        setOfflineReady(true);
      }
      setCheckingCache(false);
    };
    checkCache();
  }, [tour.slug]);

  const handleDownloadOffline = async () => {
    if (downloadingOffline || offlineReady) return;

    setDownloadingOffline(true);
    try {
      // Determine offline assets file based on tour
      const getOfflineAssetsFile = () => {
        if (tour.slug?.includes('mexico-city')) {
          return '/offline-assets-mexico-city.json';
        } else if (tour.slug === 'chicago') {
          return '/offline-assets-chicago.json';
        } else if (tour.slug?.includes('san-juan')) {
          return '/offline-assets-san-juan.json';
        }
        return null;
      };

      // Download core assets + tour-specific assets
      const tourAssetsFile = getOfflineAssetsFile();
      const assetsToDownload = tourAssetsFile
        ? ['/offline-assets-core.json', tourAssetsFile]
        : ['/offline-assets-core.json'];

      const result = await requestOfflineCache(assetsToDownload);
      if (result && result.ok) {
        setOfflineReady(true);
        setTimeout(() => setOfflineReady(false), 5000); // Reset message after 5 seconds
      } else {
        alert('Failed to download offline content. Please try again.');
      }
    } catch (error) {
      console.error('Offline download error:', error);
      alert('Failed to download offline content. Please try again.');
    } finally {
      setDownloadingOffline(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: '#000000' }}>
      <style>{`
        .ios-system-font {
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif;
        }
        .mission-gradient {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
        }
        .mission-scan-line {
          position: absolute;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, #10b981, transparent);
          animation: scan 3s ease-in-out infinite;
        }
        @keyframes scan {
          0%, 100% { top: 20%; opacity: 0; }
          50% { top: 80%; opacity: 1; }
        }
        .classified-text {
          font-family: 'Courier New', monospace;
          letter-spacing: 0.1em;
        }
      `}</style>

      {/* Background with Tour-Specific Illustration */}
      <div className="absolute inset-0" style={{
        background: '#000000',
        backgroundImage: tour.slug === 'chicago'
          ? 'url(/images/chicago-grid.svg)'
          : 'url(/images/mexico-map-outline.png)',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        opacity: 0.3
      }}>
      </div>

      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)'
      }}>
        {/* Subtle scan line effect */}
        <div className="mission-scan-line"></div>
      </div>

      {/* Menu Button */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-end p-4">
        <div className="relative">
          <button
            className="px-3 py-2 rounded-lg flex items-center gap-2"
            style={{
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              cursor: 'pointer',
              color: 'white'
            }}
            onClick={() => setShowMenuDropdown(!showMenuDropdown)}
            aria-label="Menu"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
            <span className="text-xs font-semibold">Menu</span>
          </button>

          {/* Dropdown menu */}
          {showMenuDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenuDropdown(false)}
              />
              <div
                className="absolute top-full right-0 mt-2 w-56 rounded-lg overflow-hidden shadow-2xl z-50"
                style={{
                  background: '#1e293b',
                  border: '1px solid rgba(71, 85, 105, 0.5)',
                }}
              >
                <button
                  onClick={() => window.location.href = '/'}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-gray-200 hover:bg-slate-700 transition-colors flex items-center gap-3"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Exit
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Center Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 text-center">
        {/* Classification Banner */}
        <div className="classified-text text-red-500 text-xs font-bold mb-4 tracking-widest">
          {tour.briefing?.classification || "CLASSIFIED"}
        </div>

        {/* Mission Badge */}
        {tour.badge && (
          <img
            src={tour.badge}
            alt="Mission badge"
            className="h-20 w-auto object-contain mb-4"
            style={{
              filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.3))'
            }}
          />
        )}

        {/* Mission Name */}
        <h1 className="ios-system-font text-3xl sm:text-4xl font-bold mb-2 text-white">
          {tour.missionName || tour.title}
        </h1>

        {/* Location */}
        <p className="ios-system-font text-lg mb-6 text-gray-300">
          {tour.city}
        </p>

        {/* Stats Card - Mission Style */}
        <div className="inline-flex items-center gap-4 px-6 py-3 rounded-lg mb-6" style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          boxShadow: '0 0 20px rgba(16, 185, 129, 0.1)'
        }}>
          <div className="flex flex-col items-center" style={{ width: '80px' }}>
            <span className="ios-system-font text-lg font-bold text-emerald-400">{tour.steps?.length || 10}</span>
            <span className="ios-system-font text-xs text-gray-400">Locations</span>
          </div>
          <div style={{ width: '1px', height: '32px', background: 'rgba(16, 185, 129, 0.3)' }}></div>
          <div className="flex flex-col items-center" style={{ width: '80px' }}>
            <span className="ios-system-font text-lg font-bold text-emerald-400">
              {tour.suggestedTimeLimit ? `${Math.floor(tour.suggestedTimeLimit / 60)}` : '3'}
            </span>
            <span className="ios-system-font text-xs text-gray-400">Hours</span>
          </div>
          <div style={{ width: '1px', height: '32px', background: 'rgba(16, 185, 129, 0.3)' }}></div>
          <div className="flex flex-col items-center" style={{ width: '80px' }}>
            <span className="ios-system-font text-lg font-bold text-emerald-400">{tour.distanceMiles || '1.5'}</span>
            <span className="ios-system-font text-xs text-gray-400">Miles</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <button
            onClick={() => setShowStartingPoint(true)}
            className="flex flex-col items-center gap-2"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{
              background: 'rgba(16, 185, 129, 0.2)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <span className="ios-system-font text-xs font-medium text-emerald-400">
              Mission Start
            </span>
          </button>

          <button
            onClick={handleDownloadOffline}
            className="flex flex-col items-center gap-2"
            style={{ background: 'none', border: 'none', cursor: (downloadingOffline || checkingCache) ? 'wait' : offlineReady ? 'default' : 'pointer', opacity: offlineReady ? 0.6 : 1 }}
            disabled={downloadingOffline || offlineReady || checkingCache}
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{
              background: offlineReady ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)',
              border: offlineReady ? '1px solid rgba(16, 185, 129, 0.6)' : '1px solid rgba(16, 185, 129, 0.4)',
              boxShadow: offlineReady ? '0 0 30px rgba(16, 185, 129, 0.4)' : '0 0 20px rgba(16, 185, 129, 0.2)'
            }}>
              {checkingCache ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              ) : offlineReady ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : downloadingOffline ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                  <line x1="2" y1="12" x2="6" y2="12" />
                  <line x1="18" y1="12" x2="22" y2="12" />
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </div>
            <span className="ios-system-font text-xs font-medium text-emerald-400">
              {checkingCache ? 'Checking...' : offlineReady ? 'Offline Ready' : downloadingOffline ? 'Downloading...' : 'Offline Mode'}
            </span>
          </button>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Begin Mission Button */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-6 pb-8">
        <div className="mx-auto max-w-lg">
          <button
            onClick={onStart}
            className="w-full ios-system-font font-semibold text-base"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              borderRadius: '13px',
              padding: '18px',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              cursor: 'pointer',
              minHeight: '56px',
              boxShadow: '0 0 30px rgba(16, 185, 129, 0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Begin Mission →
          </button>
        </div>
      </div>

      {/* Starting Point Modal - iOS Sheet */}
      {showStartingPoint && (
        <div
          className="fixed z-50 flex items-end"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
          onClick={() => setShowStartingPoint(false)}
        >
          <div
            className="w-full max-w-lg mx-auto rounded-t-3xl overflow-hidden"
            style={{
              background: '#1e293b',
              maxHeight: '80vh',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div style={{
                width: '36px',
                height: '5px',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.3)'
              }}></div>
            </div>

            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 50px)' }}>
              <h2 className="ios-system-font text-2xl font-bold mb-4 text-white">
                Mission Start Point
              </h2>

              <div className="mb-6">
                <div className="ios-system-font text-lg font-semibold mb-2 text-emerald-400">
                  {tour.startingPoint?.name ? tour.startingPoint.name.charAt(0).toUpperCase() + tour.startingPoint.name.slice(1) : ''}
                </div>
                <div className="ios-system-font text-base mb-4 text-gray-400">
                  {tour.startingPoint?.details}
                </div>

                <p className="ios-system-font text-sm text-gray-300">
                  Your mission begins outside the entrance of {tour.startingPoint?.name || 'the starting point'}. Once you arrive, you can click "Begin Mission" button.
                </p>
              </div>

              <button
                onClick={() => setShowStartingPoint(false)}
                className="w-full ios-system-font font-semibold text-base mb-4"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  borderRadius: '13px',
                  padding: '16px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Acknowledged
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
